/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/qaTemplateCatalog.ts
 * FILE_ID: QA-TEMPLATES-CATALOG-001
 * PURPOSE: Pure grouping, search, and selection counts for the template picker.
 * LANDMINE: Labels come from the API. This file only orders location_type keys.
 */
import type {
  QaTemplateDetail,
  QaTemplateInsertPayload,
  QaTemplateSummary,
} from '../../../qa/qaTemplateTypes'
export const BLANK_TEMPLATE_ID = 'blank'

export const BLANK_TEMPLATE: QaTemplateSummary = {
  id: BLANK_TEMPLATE_ID,
  name: 'Blank',
  location_type: 'blank',
  location_type_label: 'Blank',
  section_count: 1,
  item_count: 1,
}

export const BLANK_TEMPLATE_DETAIL: QaTemplateDetail = {
  ...BLANK_TEMPLATE,
  sections: [{
    id: 'blank-checklist',
    section_number: '1',
    title: 'checklist',
    items: [{
      id: 'blank-item',
      item_number: '1',
      title: 'Checklist Step',
    }],
  }],
}

export const QA_TEMPLATE_LOCATION_ORDER = [
  'blank',
  'roof',
  'floors',
  'underslab',
  'walls',
  'below_grade',
  'internal_wet',
  'balcony',
  'planter',
  'handover',
  'testing',
  'required_attachments',
  'general',
] as const

export interface QaTemplateLocationGroup {
  location_type: string
  label: string
  subtitle: string
  count: number
  templates: QaTemplateSummary[]
}

function haystack(value: string | undefined): string {
  return (value ?? '').toLowerCase()
}

export function groupByLocationType(
  templates: QaTemplateSummary[],
  opts?: { includeBlank?: boolean },
): QaTemplateLocationGroup[] {
  const includeBlank = opts?.includeBlank !== false
  const source = !includeBlank || templates.some((row) => row.id === BLANK_TEMPLATE_ID)
    ? templates
    : [...templates, BLANK_TEMPLATE]
  const buckets = new Map<string, QaTemplateSummary[]>()
  for (const template of source) {
    const key = template.location_type || ''
    const list = buckets.get(key) ?? []
    list.push(template)
    buckets.set(key, list)
  }
  const keys = [
    ...QA_TEMPLATE_LOCATION_ORDER.filter((key) => buckets.has(key)),
    ...[...buckets.keys()].filter((key) => !QA_TEMPLATE_LOCATION_ORDER.includes(key as typeof QA_TEMPLATE_LOCATION_ORDER[number])),
  ]
  return keys.map((location_type) => {
    const rows = buckets.get(location_type) ?? []
    const systems = [...new Set(rows.map((row) => row.system).filter(Boolean))] as string[]
    const shown = systems.slice(0, 3)
    return {
      location_type,
      label: rows[0]?.location_type_label || 'Other',
      subtitle: shown.join(', ') + (systems.length > 3 ? '…' : ''),
      count: rows.length,
      templates: rows,
    }
  })
}

export function filterCatalog(
  templates: QaTemplateSummary[],
  detailsById: Record<string, QaTemplateDetail>,
  query: string,
): QaTemplateSummary[] {
  const needle = haystack(query).trim()
  if (!needle) return templates
  return templates.filter((template) => {
    if (haystack(template.name).includes(needle) || haystack(template.system).includes(needle)) {
      return true
    }
    const detail = detailsById[template.id]
    if (!detail) return false
    return detail.sections.some((section) => (
      haystack(section.title).includes(needle)
      || haystack(section.default_product).includes(needle)
      || section.items.some((item) => (
        haystack(item.title).includes(needle) || haystack(item.product).includes(needle)
      ))
    ))
  })
}

export function countSelection(
  detail: QaTemplateDetail | null,
  selection: { sectionIds: Set<string>; itemIds: Record<string, Set<string>> },
  areaCount: number,
): { checklists: number; steps: number; stepsTimesAreas: number } {
  if (!detail) return { checklists: 0, steps: 0, stepsTimesAreas: 0 }
  let checklists = 0
  let steps = 0
  for (const section of detail.sections) {
    const items = selection.itemIds[section.id]
    if (!items || items.size === 0) continue
    checklists += 1
    steps += items.size
  }
  return { checklists, steps, stepsTimesAreas: steps * Math.max(areaCount, 0) }
}

export function buildInsertPayload(
  detail: QaTemplateDetail,
  targetAreaIds: string[],
  selectedItemIds: Record<string, Set<string>>,
  sectionTitleOverrides: Record<string, string>,
  itemTitleOverrides: Record<string, string>,
  inspectionId: string,
): QaTemplateInsertPayload {
  const sections = detail.sections.flatMap((section) => {
    const items = [...(selectedItemIds[section.id] ?? [])]
    if (items.length === 0) return []
    return [{
      section_id: section.id,
      ...(sectionTitleOverrides[section.id] ? { title: sectionTitleOverrides[section.id] } : {}),
      items: items.map((item_id) => ({
        item_id,
        ...(itemTitleOverrides[item_id] ? { title: itemTitleOverrides[item_id] } : {}),
      })),
    }]
  })
  return {
    area_ids: targetAreaIds,
    sections,
    blank_products: true,
    ensure_location_type: targetAreaIds.length === 0,
    inspection_id: inspectionId,
  }
}
